import { useState, useEffect, useRef } from 'react';

/**
 * useInView — shared IntersectionObserver hook (DESIGN-SPEC §3.6).
 *
 * @param {Object}  [opts]
 * @param {number}  [opts.threshold=0.15]                    IntersectionObserver threshold.
 * @param {string}  [opts.rootMargin='0px 0px -10% 0px']     IntersectionObserver rootMargin.
 * @param {boolean} [opts.once=true]                         Stop observing after the first entry.
 * @returns {{ ref: React.RefObject<Element>, inView: boolean }}
 *
 * Reduced-motion guard: when the user prefers reduced motion, `inView` starts
 * `true` and NO observer is registered — content is revealed immediately.
 * Motion = native IntersectionObserver only; zero extra dependency.
 */
export default function useInView(opts = {}) {
  const {
    threshold = 0.15,
    rootMargin = '0px 0px -10% 0px',
    once = true,
  } = opts;

  // Detect reduced-motion synchronously so initial `inView` is correct on first paint.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ref = useRef(null);
  const [inView, setInView] = useState(prefersReducedMotion);

  useEffect(() => {
    // Reduced motion: already revealed, register no observer.
    if (prefersReducedMotion) return undefined;

    const node = ref.current;
    if (!node) return undefined;

    // Graceful fallback if IntersectionObserver is unavailable: reveal immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          setInView(true);
          if (once) observer.unobserve(entry.target);
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [threshold, rootMargin, once, prefersReducedMotion]);

  return { ref, inView };
}
